import { cn } from '@/lib/utils';
import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { RadioGroup } from '@headlessui/react';
import { CheckCircle as CheckCircleIcon } from 'lucide-react';
import React from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { PatientClinic } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';

type Props = {
  clinics: PatientClinic[];
  onSelect: (clinic: PatientClinic) => void;
  language?: 'en' | 'es';
};

const ClinicSelect: React.FC<Props> = ({ clinics, onSelect, language = 'en' }) => {
  return (
    <div className="mx-auto max-w-lg py-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary-color)] text-white">
          <Icon icon={Icons.building2} className="h-7 w-7" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.selectClinic[language]}
        </h2>
        <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.selectClinicDescription[language]}
        </p>
      </div>
      <RadioGroup onChange={onSelect}>
        <div className="grid grid-cols-1 gap-y-3">
          {clinics.map(clinic => (
            <RadioGroup.Option
              key={clinic.clinicId}
              value={clinic}
              className={({ checked, active }) =>
                cn(
                  'portal-card relative flex cursor-pointer p-4 focus:outline-none transition-shadow',
                  checked ? 'ring-2 ring-[var(--primary-color)]' : '',
                  active ? 'ring-2 ring-[var(--primary-color)]' : ''
                )
              }
            >
              {({ checked }) => (
                <>
                  <span className="flex flex-1 items-center gap-4">
                    <FallbackImage
                      src={clinic.logo}
                      alt={clinic.clinicName}
                      imgClassName="h-11 w-11 rounded-xl object-contain"
                      fallback={
                        <span
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                          style={{ backgroundColor: clinic.primaryColor || '#77bd43' }}
                        >
                          <span className="text-sm font-bold text-white">
                            {clinic.clinicName?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </span>
                      }
                    />
                    <div className="flex flex-col">
                      <RadioGroup.Label
                        as="span"
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {clinic.clinicName}
                      </RadioGroup.Label>
                      {clinic.city && clinic.state && (
                        <RadioGroup.Description
                          as="span"
                          className="text-xs text-gray-400 dark:text-gray-500"
                        >
                          {clinic.city}, {clinic.state}
                        </RadioGroup.Description>
                      )}
                    </div>
                  </span>
                  <CheckCircleIcon
                    className={cn(checked ? 'h-5 w-5 text-[var(--primary-color)]' : 'hidden')}
                    aria-hidden="true"
                  />
                </>
              )}
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default ClinicSelect;
